import { useTheme } from "@emotion/react";
import { Box, Button, Typography } from "@mui/material";
import axios from "axios";
import { useCallback, useEffect, useState } from "react";
import {
  ArrowRight,
  Check,
  PencilSquare,
  TrashFill,
  X,
} from "react-bootstrap-icons";
import toast from "react-hot-toast";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import DeleteDialog from "../../../Components/DeleteDialog/DeleteDialog";
import DataTable from "../../../Components/DataTable/DataTable";
import styles from "./heading.module.css"
function ViewsTable() {
  const [data, setData] = useState();
  const [openDialog, setOpenDialog] = useState(false);
  const [loading, setLoading] = useState(false);
  const [deleteId, setDeleteId] = useState(null);

  const role = useSelector((state) => state.auth.role.roleName);
  const userId = useSelector((state) => state.auth.user.userId);
  const navigate = useNavigate();
  const theme = useTheme();

  let requestUrl =
    role === "SuperAdmin"
      ? `${process.env.REACT_APP_API_URL}/view/`
      : role === "Admin"
      ? `${process.env.REACT_APP_API_URL}/view/admin`
      : `${process.env.REACT_APP_API_URL}/view/user/${userId}`;

  const now = new Date();
  const fiveMinutes = 5 * 60 * 1000;
  const threeDays = 3 * 24 * 60 * 60 * 1000;
  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(requestUrl, {
        withCredentials: true,
      });
      if (res) {
        setData(res.data);
        setLoading(false);
      }
    } catch (error) {
      setLoading(false);
      toast.error(error.message);
    }
  }, [requestUrl]);

  useEffect(() => {
    fetchData();
  }, [fetchData, role]);

  const columns = [
    { field: "quantity", headerName: "عدد المشاهدات", width: 150 },
    { field: "finalAmount", headerName: "المبلغ النهائي", width: 150 },
    {
      field: "discounted",
      headerName: "مع خصم؟",
      width: 150,
      renderCell: (params) => {
        return (
          <Box>
            {params.value ? (
              <Check color="black" size={32} />
            ) : (
              <X color="gray" size={32} />
            )}
          </Box>
        );
      },
    },
    {
      field: "DiscountReason",
      headerName: "سبب الخصم",
      width: 160,
      valueGetter: (value, row) => {
        if (row.DiscountReason !== null) {
          return row.DiscountReason?.reason;
        } else if (row.DiscountReason === null && row.customReason !== null) {
          return row.customReason;
        } else {
          return null;
        }
      },
    },
    {
      field: "date",
      headerName: "التاريخ",
      width: 200,
      valueGetter: (value) => {
        const date = new Date(value);
        return date.toISOString().split("T")[0];
      },
    },
    {
      field: "PaymentMethod",
      headerName: "طريقة الدفع",
      width: 150,
      valueGetter: (value) => {
        return value ? value.name : "محذوف";
      },
    },
    {
      field: "Site",
      headerName: "الموقع",
      width: 150,
      valueGetter: (value) => {
        return value ? value.name : "محذوف";
      },
    },
    {
      field: "User",
      headerName: "المستخدم",
      width: 150,
      valueGetter: (value) => {
        return value ? value.name : "";
      },
    },
    {
      field: "ViewType",
      headerName: "نوع المشاهدة",
      width: 150,
      valueGetter: (value) => {
        return value ? value.name : "محذوف";
      },
    },
    {
      field: "WorkTime",
      headerName: "مدة العمل",
      width: 160,
      valueGetter: (value, row) => {
        if (row.WorkTime !== null) {
          return row.WorkTime?.duration;
        } else if (row.WorkTime === null && row.customWorkTime !== null) {
          return row.customWorkTime;
        } else {
          return null;
        }
      },
    },
    {
      field: "action",
      headerName: "",
      sortable: false,
      disableColumnMenu: true,
      width: 150,
      renderCell: (params) => {
        const createdAt = new Date(params.row.createdAt);
        return (
          <Box
            sx={{
              width: "100%",
              height: "100%",
              display: "flex",
              justifyContent: "space-evenly",
              alignItems: "center",
            }}
          >
            {role === "SuperAdmin" ||
            (role === "Admin" && now - createdAt < threeDays) ||
            (role === "Employee" && now - createdAt < fiveMinutes) ? (
              <Button
                variant="contained"
                sx={{
                  width: "36px",
                  height: "36px",
                  padding: 0,
                  margin: 0,
                  minWidth: "auto",
                  backgroundColor: theme.palette.neutral[50],
                  "&:hover": {
                    backgroundColor: theme.palette.neutral[100],
                  },
                }}
                onClick={() => handleEditClick(params.row)}
                disableElevation
              >
                <PencilSquare size={16} color={theme.palette.primary[600]} />
              </Button>
            ) : (
              ""
            )}
            {role === "SuperAdmin" ? (
              <Button
                variant="contained"
                sx={{
                  width: "36px",
                  height: "36px",
                  padding: 0,
                  margin: 0,
                  minWidth: "auto",
                  backgroundColor: theme.palette.neutral[50],
                  "&:hover": {
                    backgroundColor: theme.palette.neutral[100],
                  },
                }}
                onClick={() => handleDeleteClick(params.row.id)} // Pass the id here
                disableElevation
              >
                <TrashFill size={16} color={theme.palette.primary[600]} />
              </Button>
            ) : (
              ""
            )}
          </Box>
        );
      },
    },
  ];

  const handleCloseDialog = () => setOpenDialog(false);

  const handleEditClick = (data) => {
    navigate(`/app/views/edit/${data.id}`, { state: data });
  };

  const handleDeleteClick = (id) => {
    setDeleteId(id); // Set the id of the record to be deleted
    setOpenDialog(true); // Open the dialog
  };

  const handleDelete = async () => {
    if (deleteId) {
      try {
        const res = await axios.delete(
          `${process.env.REACT_APP_API_URL}/view/${deleteId}`,
          {
            withCredentials: true,
          }
        );
        if (res) {
          toast.success("تم حذف بيانات تلك المشاهدات بنجاح!");
          setData((prevData) =>
            prevData.filter((item) => item.id !== deleteId)
          );
          setDeleteId(null);
        }
      } catch (error) {
        toast.error(error.message);
        setDeleteId(null);
      }
    }
    setOpenDialog(false);
  };

  return (
    <div>
      <DeleteDialog
        open={openDialog}
        handleDelete={handleDelete}
        handleCloseDialog={handleCloseDialog}
        message={"هل أنت متأكد من أنك تريد أن تحذف هذه المعلومات؟"}
      />
      <div className={styles.heading}>
        <Button
          onClick={() => navigate(-1)}
          variant="contained"
          sx={{
            width: "48px",
            height: "48px",
            padding: 0,
            margin: 0,
            minWidth: "auto",
            marginRight: "12px",
          }}
        >
          <ArrowRight size={24} />
        </Button>
        <Typography
          variant="h4"
          color={theme.palette.neutral[900]}
          fontWeight={700}
        >
          جدول المشاهدات
        </Typography>
      </div>
      <DataTable dataColumns={columns} data={data} loading={loading} />
    </div>
  );
}

export default ViewsTable;
